import type { MouseEvent } from 'react'

/** Kart içindeki eylem alanının işaretçisi (`ui.css` → `.ui-card-actions`). */
const CARD_ACTIONS_SELECTOR = '.ui-card-actions'

/**
 * Tıklanabilir kartın (`role="button"`) `onClick`'i asıl işini yapmalı mı? Kart gövdesinden gelen
 * tıklama `true`, **eylem alanından** ya da **kartın DOM alt ağacı dışından** gelen tıklama `false`
 * döner.
 *
 * <p><b>Neden sarmalayıcıda `stopPropagation` değil.</b> Önceki çözüm eylem alanına
 * `onClick={(e) => e.stopPropagation()}` koyuyordu. O sarmalayıcı `<span>`/`<div>` semantik olarak
 * etkileşimli değildir; üzerine tıklama dinleyicisi koymak Sonar `typescript:S1082`'yi (jsx-a11y
 * `click-events-have-key-events`) **Bug** olarak üretir. THY'nin kalite kapısı tek bir şartla
 * ("Reliability Rating = A", yani sıfır açık Bug) düşüyor ve 31.08.2026'da `ceo-frontend/release`
 * kapısı **yalnızca** bu üç bulgu yüzünden kırmızıydı. Sarmalayıcıya klavye dinleyicisi eklemek
 * çözüm değildir: o da `S6848`'i besler ve `0717f6ca` tam o yüzden geri alındı. Kalıcı çözüm
 * sarmalayıcıyı **handler'sız** bırakıp elemeyi kartta yapmaktır.
 *
 * <p><b>15.09.2026 — bu yardımcının kullanıcı listesi İKİYE düştü.</b> THY incelemesi eleme yaklaşımının
 * kök sorunu çözmediğini gösterdi: eleme yalnız DAVRANIŞI düzeltir, kart `role="button"` kaldıkça eylem
 * denetimi hâlâ interaktif bir kontrolün İÇİNDEdir ve ARIA bunu yasaklar. Gezinme amaçlı kartlar bu yüzden
 * **overlay-link** biçimine taşındı (anayasa → kart kuralı) ve orada eleme GEREKMEZ — eleyecek bir ata
 * handler'ı kalmadı. Eski emsal olarak anılan `MeetingGroupCard` ve `MeetingList` de taşınanlar arasındadır.
 *
 * <p>Geriye kalan iki kullanıcı `CountriesPage` (kart = satır SEÇİMİ) ve `TicketTable` (kart = AÇ/KAPA)
 * bilinçle eski biçimdedir: tıklamaları gezinme olmadığı için doğru ARIA karşılıkları overlay-link değil.
 * Bu dosya onlar için canlıdır; yeni bir GEZİNME kartında kullanılMAZ.
 *
 * <p><b>İki denetim, iki ayrı soru:</b>
 *
 * <ul>
 *   <li><b>{@code contains}</b> — tıklama bu kartın DOM alt ağacında mı gerçekleşti? React olayları
 *       <b>bileşen</b> ağacında kabarcıklanır, DOM ağacında değil; kartın JSX'i içinden bir portal
 *       (ör. {@code appendTo={document.body}}) çizilirse oradaki tıklama karta ulaşır ama DOM'da
 *       kartın dışındadır. Böyle bir tıklama {@code .ui-card-actions} atası da taşımaz, yani
 *       {@code closest} onu "gövde tıklaması" sanardı.
 *       <p><b>Ölçüm (31.08.2026):</b> bugün kartlardaki tek portal PrimeReact {@code Menu}'dür ve o
 *       kendi {@code onItemClick}'inde {@code stopPropagation} çağırıyor
 *       ({@code primereact/menu/menu.esm.js} — {@code url}'siz her öğe için), dolayısıyla kebab
 *       menüleri bu denetime <b>bugün ihtiyaç duymuyor</b>. Denetim yine de duruyor: ifade ettiği
 *       şey bir kütüphane ayrıntısı değil, kartın sözleşmesidir — "bu kartın içinde gerçekleşmeyen
 *       tıklama bu kartın tıklaması değildir". Bariyeri {@code cardClick.test.tsx}'tedir.
 *   <li><b>{@code closest}</b> — tıklama kartın kendi eylem alanında mı? Buton/switch kartın alt
 *       ağacı İÇİNDEDİR, dolayısıyla {@code contains} onları yakalayamaz. Masaüstü tablo dalındaki
 *       karşılığı `onRowClick` içindeki {@code .tk-acts} denetimidir.
 * </ul>
 *
 * <p>Klavye tarafının karşılığı kartların {@code onKeyDown}'undaki {@code e.target === e.currentTarget}
 * denetimidir (bariyer: {@code cardActionGuard.test.tsx}). Orada bu yardımcı kullanılMAZ: Enter/Space
 * kartın kendisine odaklanmışken gelir, dolayısıyla eşitlik denetimi hem daha dar hem daha ucuzdur.
 */
export function isCardBodyClick(e: MouseEvent<HTMLElement>): boolean {
  const target = e.target
  // Element değilse (beklenmez) kartın işini engelleme — masaüstü tablo dalındaki `.tk-acts`
  // denetimi de aynı yönde davranır.
  if (!(target instanceof Element)) return true
  if (!e.currentTarget.contains(target)) return false
  return target.closest(CARD_ACTIONS_SELECTOR) === null
}
